/**
 * Resolved-feature read foundation for the learner app (P-SCH-V1, ADR-0103).
 *
 * `apps/learn` had no `resolvedFeatures` read path — `apps/admin` runs
 * `resolveFeatures` client-side over `configuration.get`, which a learner can't
 * call. This hook reads the LEARNER-accessible, module-gated server proc
 * `trpc.learning.meta.resolvedFeatures`, which resolves the EXISTING vertical
 * feature flags (per ADR-0083's 3-axis gating) for the learner's tenant.
 *
 * This is the FOUNDATION only — later phases (Schoology V1 nav simplification)
 * read this to HIDE student-side surfaces (Mentor / Family / Practice / heavy
 * predictive cards) for coaching-centre tenants. Nothing is gated here yet.
 *
 * The query is disabled while signed out, so a shared device never leaks one
 * learner's resolved flags to the next.
 */
import type { inferRouterOutputs } from '@trpc/server';
import { trpc, type AppRouter } from './trpc.js';
import { useCurrentUser } from './auth.js';

/**
 * The resolved vertical feature flags for the current learner's tenant — a
 * `Record<VerticalFeatureKey, boolean>` inferred straight off the tRPC contract
 * (`@mangrove/api-contract`), so it stays in lock-step with the server.
 */
export type ResolvedFeatures =
  inferRouterOutputs<AppRouter>['learning']['meta']['resolvedFeatures'];

/**
 * The resolved feature flags for the current learner's tenant. `data` is
 * `undefined` while loading or signed out.
 *
 * NOTE: we return a NARROWED `{ data, isPending }` rather than the raw
 * `useQuery` result on purpose. Annotating the hook with the full
 * `UseTRPCQueryResult` type drags a non-portable `@trpc/react-query/dist/*`
 * path into the inferred signature (TS2742), and `ReturnType<typeof useQuery>`
 * instantiates the generic with defaults → `data: {}` (every flag read fails).
 * A narrowed object with the contract-inferred `ResolvedFeatures` is both
 * portable and correctly typed — and it's all any consumer uses.
 */
export function useResolvedFeatures(): {
  data: ResolvedFeatures | undefined;
  isPending: boolean;
} {
  const me = useCurrentUser();
  const enabled = Boolean(me.data);
  const query = trpc.learning.meta.resolvedFeatures.useQuery(undefined, { enabled });
  return { data: query.data, isPending: query.isPending };
}
